import React, { useEffect, useState } from 'react';
import { useSearchParams, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useSubscription } from '../SubscriptionContext';
import { apiUrl } from '../config/apiBase';
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { checkSubscription } = useSubscription();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [authLoading, setAuthLoading] = useState(true);

  const sessionId = searchParams.get('session_id');

  // Wait for authentication to load before deciding on redirect
  useEffect(() => {
    const timeout = setTimeout(() => {
      setAuthLoading(false);
    }, 2000); // Give 2 seconds for auth to load

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const verifySession = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        // Verify the session with Stripe directly (doesn't require authentication)
        const response = await fetch(apiUrl(`/api/stripe/verify-session/${sessionId}`));
        
        if (response.ok) {
          const data = await response.json();
          setSessionData(data);
          
          // If user is authenticated, refresh subscription
          if (user) {
            await checkSubscription();
          }
        } else {
          throw new Error('Failed to verify session');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error verifying session:', err);
        setError('Failed to verify payment session');
        setLoading(false);
      }
    };

    verifySession();
  }, [sessionId, user, checkSubscription]);

  // Auto-redirect countdown
  useEffect(() => {
    if (!loading && !error && sessionData) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Redirect based on authentication status
            if (user) {
              navigate('/dashboard');
            } else {
              // If not authenticated, go to home page with a message
              navigate('/?payment=success');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [loading, error, sessionData, user, navigate]);

  // Redirect to login if user is not authenticated after loading
  if (!authLoading && !user && !sessionData) {
    return <Navigate to="/" replace />;
  }

  if (loading || authLoading) {
    return (
      <div className="payment-success-container">
        <div className="payment-success-card">
          <div className="loading-spinner"></div>
          <h2>Verifying your payment...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-container">
        <div className="payment-success-card error">
          <div className="icon-wrapper">
            <FaExclamationTriangle className="react-icon error-icon-color" />
          </div>
          <h2>Payment Verification Failed</h2>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/pricing')}
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-container">
      <div className="payment-success-card">
        <div className="icon-wrapper">
          <FaCheckCircle className="react-icon success-icon-color" />
        </div>
        <h2>Payment Successful!</h2>
        <p>Thank you for subscribing to the Standard Plan!</p>
        <p>Your subscription is now active and you can start using all premium features.</p>
        
        <div className="countdown-display">
          <p>Redirecting to {user ? 'dashboard' : 'home'} in <strong>{countdown}</strong> seconds...</p>
          <div className="countdown-bar">
            <div 
              className="countdown-progress" 
              style={{ width: `${(countdown / 5) * 100}%` }}
            ></div>
          </div>
        </div>
        
        <div className="success-actions">
          <button 
            onClick={() => {
              if (user) {
                navigate('/dashboard');
              } else {
                // If not authenticated, go to home
                navigate('/');
              }
            }}
            className="dashboard-button"
          >
            {user ? 'Go to Dashboard Now' : 'Go to Home'}
          </button>
          <button 
            onClick={() => navigate('/')}
            className="home-button"
          >
            Back to Home
          </button>
        </div>

        {sessionData && (
          <div className="session-info">
            <p className="session-id">Session ID: {sessionData.sessionId}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;